import { Observable, Subject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import * as moment from 'moment';

import {
    ServerResponse,
    HttpStatusCodesEnum,
    DocumentStatusesEnum,
    AuthApiService,
    CompanyExtendedModel,
    CompanyDocumentModel,
    CompanyDocumentsAtomicService,
    CompanyDocumentAndFilesInterface,
    CompanyDocumentAndFilesModel,
    ApiResponse,
    ApiError,
    CompanyDocumentInterface
} from '@spa/api';

import { InArrayValidatorKey, RequiredValidatorKey } from '@spa/validators';

import { Helper, SelectControl } from '../../../../../shared/classes';

import { CompanyDocumentsFormErrorStatesEnum } from '../../../enums';

import { CompanyDocumentsControl } from '../../../../../../assets/descriptors';

export class CompanyDocumentsForm {
    protected readonly authApiService: AuthApiService;
    protected readonly company: CompanyExtendedModel;
    protected readonly companyDocumentsAtomicService?: CompanyDocumentsAtomicService;

    public document: CompanyDocumentModel;
    public documentTypeControl: SelectControl;
    public documentSubject: Subject<string>;
    public documentErrorsSubject: Subject<string[]>;
    public documentFile: File;
    public reviewSummary: string;
    public hiddenDocument: boolean;

    constructor(
        company: CompanyExtendedModel,
        authApiService: AuthApiService,
        companyDocumentsAtomicService: CompanyDocumentsAtomicService
    ) {
        this.company = company;
        this.authApiService = authApiService;
        this.companyDocumentsAtomicService = companyDocumentsAtomicService;

        this.documentSubject = new Subject();
        this.documentErrorsSubject = new Subject();
        this.documentFile = null;
        this.document = null;
        this.reviewSummary = '';
        this.hiddenDocument = false;
    }

    private validateDocumentFile(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        const documentSubjectErrors = Helper.validateFile(this.documentFile, messageFunction);

        this.documentErrorsSubject.next(documentSubjectErrors);

        return (documentSubjectErrors.length === 0);
    }

    private validateControl(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        const formsControlValue = this.documentTypeControl.value as string;

        if (!formsControlValue) {
            this.documentTypeControl.setError(messageFunction(this.documentTypeControl.placeholder)[RequiredValidatorKey]);

            return false;
        }

        const documentTypeIds = this.getDocumentTypeIds();

        if (documentTypeIds.indexOf(formsControlValue) < 0) {
            this.documentTypeControl.setError(messageFunction(this.documentTypeControl.placeholder)[InArrayValidatorKey]);

            return false;
        }

        return true;
    }

    private registerDocumentWithAtomicService(file: File, refreshDocument: boolean): ServerResponse<CompanyDocumentModel> {
        const createCompanyDocumentAndFilesModel = this.getCompanyDocumentInputModel(file);

        return this.companyDocumentsAtomicService.atomicCreateAndRegister(createCompanyDocumentAndFilesModel)
            .pipe(
                switchMap((response: ApiResponse<ApiError[] | CompanyDocumentInterface>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponseObservable<CompanyDocumentInterface>(response,
                            CompanyDocumentsFormErrorStatesEnum.DocumentRegistrationFailed);
                    }

                    const data = response.data as CompanyDocumentInterface;
                    const model = new CompanyDocumentModel(data);

                    if (!this.company.documents) {
                        this.company.documents = [];
                    }

                    if (refreshDocument) {
                        this.documentSubject.next(model.fileStorageIds[0]);
                    }

                    this.documentFile = null;
                    this.company.documents.push(model);

                    return Helper.createSuccessResponseObservable<CompanyDocumentModel>(model);
                }));
    }

    private updateDocument(file: File, refreshDocument: boolean
    ): Observable<ApiResponse<ApiError[] | CompanyDocumentModel>> {
        const documentToUpdate = this.getUpdateDocumentModel(file);

        return this.companyDocumentsAtomicService.atomicCreateAndRegister(documentToUpdate).pipe(
            map((response: ApiResponse<ApiError[] | CompanyDocumentInterface>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse<CompanyDocumentInterface>(
                        response,
                        CompanyDocumentsFormErrorStatesEnum.UpdateDocumentFailed
                    );
                }
                this.documentFile = null;

                if (refreshDocument) {
                    this.documentSubject.next(this.document.fileStorageIds[0]);
                }
                this.company.documents.push(this.document);

                return Helper.createSuccessResponse<CompanyDocumentModel>(this.document);
            }));
    }

    private getUpdateDocumentModel(file: File): CompanyDocumentAndFilesModel {

        const clonedModel = this.document.serialize();
        const createCompanyDocumentAndFilesInterface: CompanyDocumentAndFilesInterface = {
            documentType: clonedModel.document_type,
            subDocumentType: clonedModel.sub_document_type,
            fileName: clonedModel.file_name,
            files: [file],
            fileStorageIds: clonedModel.file_storage_id,
            uploadedBy: this.authApiService.getAuthenticatedUser().id,
            companyId: this.company.id,
            reviewSummary: clonedModel.review_summary,
            isHiddenForClient: clonedModel.is_hidden_for_client,
        };

        return new CompanyDocumentAndFilesModel(createCompanyDocumentAndFilesInterface);
    }
    protected getCompanyDocumentInputModel(file: File): CompanyDocumentAndFilesModel {
        const companyDocumentUploadModel: CompanyDocumentAndFilesInterface = {
            documentType: this.documentTypeControl.value as string,
            companyId: this.company.id,
            fileName: file.name,
            subDocumentType: null,
            uploadedBy: this.authApiService.getAuthenticatedUser().id,
            reviewSummary: this.reviewSummary,
            isHiddenForClient: this.hiddenDocument,
            files: [file],
            fileStorageIds: ['']
        };

        return new CompanyDocumentAndFilesModel(
            companyDocumentUploadModel
        );
    }

    protected getDocumentTypeIds(): string[] {
        return Helper.enumsHelper().getCompanyDocumentTypes().getTypeKeys();
    }

    protected getInitialControlStructure(): SelectControl {
        return CompanyDocumentsControl();
    }

    resetDocument(): void {
        this.document = null;
        this.documentSubject.next(null);
        this.documentErrorsSubject.next([]);
    }

    onDocumentUpload(file: File): void {
        this.documentFile = file;
    }

    initFormControl(value = null, placeholder = ''): void {
        this.documentTypeControl = this.getInitialControlStructure();

        this.documentTypeControl.value = value;
        this.documentTypeControl.placeholder = !placeholder ? this.documentTypeControl.placeholder : placeholder;
    }

    initDocument(documents: CompanyDocumentModel[]): void {
        if (documents && documents.length > 0) {
            const waitingForApprovalStatuses =
                [DocumentStatusesEnum.New, DocumentStatusesEnum.InReview] as number[];

            const filteredDocuments = documents
                .filter((document: CompanyDocumentModel) => {
                    return (
                        (this.getDocumentTypeIds().indexOf(document.documentType) >= 0) &&
                        (waitingForApprovalStatuses.indexOf(document.status) >= 0)
                    );
                })
                .sort((current: CompanyDocumentModel, next: CompanyDocumentModel) => {
                    return moment(next.uploadStamp).unix() - moment(current.uploadStamp).unix();
                });

            if (filteredDocuments[0]) {
                this.document = filteredDocuments[0];

                this.documentTypeControl.value = filteredDocuments[0].documentType;
            }

            setTimeout(() => {
                if (filteredDocuments[0]) {
                    this.documentSubject.next(filteredDocuments[0].fileStorageIds[0]);
                }
            }, 0);
        }
    }

    validateForm(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        return [
            this.validateDocumentFile(messageFunction),
            this.validateControl(messageFunction),
        ].indexOf(false) < 0;
    }

    createRequest(refreshDocument = true): ServerResponse<CompanyDocumentModel> {
        if (!this.document) {
            return this.registerDocumentWithAtomicService(this.documentFile, refreshDocument);
        }

        if (this.document && (this.document.fileStorageIds[0] !== this.documentFile.name)) {
            return this.updateDocument(this.documentFile, refreshDocument);
        }

        return Helper.createSuccessResponseObservable<CompanyDocumentModel>(null);
    }
}
